import React, { useState, useEffect, useCallback } from 'react';
import { Clock } from 'lucide-react';

import ApprovalTable from '../../components/credit/approval/ApprovalTable';
import ApprovalDialog from '../../components/credit/approval/ApprovalDialog';
import creditSubmissionApi, { CreditSubmission } from '../../api/creditSubmissionApi';
import debtorApi from '../../api/debtorApi';
import { useToast } from '../../context/ToastContext';

interface Application {
  id: string;
  application_number: string;
  debtor_id: string;
  credit_type: string;
  requested_amount: number;
  total_score: number;
  analyst_recommendation: string;
}

interface Debtor {
  id: number;
  full_name: string;
}

export default function ApprovalList() {
  const { showSuccess, showError } = useToast();
  const [applications, setApplications] = useState<Application[]>([]);
  const [debtors, setDebtors] = useState<Debtor[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject' | null>(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [submissions, dbtrs] = await Promise.all([
        creditSubmissionApi.getAll({ status: 'submitted' }),
        debtorApi.getAll(),
      ]);

      // Transform CreditSubmission to Application format
      const transformedApps: Application[] = submissions.map((sub: CreditSubmission) => ({
        id: sub.id.toString(),
        application_number: sub.submission_number,
        debtor_id: sub.debtor_id?.toString() || '',
        credit_type: sub.application_type || '',
        requested_amount: sub.requested_amount || 0,
        total_score: 0, // TODO: Calculate from scoring model
        analyst_recommendation: '', // TODO: Get from analysis
      }));

      setApplications(transformedApps);
      setDebtors(dbtrs);
    } catch (error: any) {
      console.error('Error loading data:', error);
      showError(error?.response?.data?.error || error?.message || 'Gagal memuat data pengajuan');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getDebtorName = (debtorId: string) => {
    const id = parseInt(debtorId);
    return debtors.find(d => d.id === id)?.full_name || '-';
  };

  const openApprovalDialog = (app: Application, action: 'approve' | 'reject') => {
    setSelectedApp(app);
    setApprovalAction(action);
    setApprovalNotes('');
    setApprovalDialogOpen(true);
  };

  const handleApproval = async () => {
    if (!selectedApp) return;

    setProcessing(true);
    try {
      const newStatus = approvalAction === 'approve' ? 'approved' : 'rejected';
      await creditSubmissionApi.update(parseInt(selectedApp.id), {
        status: newStatus,
        // TODO: Add approval_notes field if exists in schema
      });

      showSuccess(`Pengajuan berhasil ${approvalAction === 'approve' ? 'disetujui' : 'ditolak'}`);
      loadData();
      setApprovalDialogOpen(false);
    } catch (error: any) {
      console.error('Error processing approval:', error);
      showError(error?.message || 'Gagal memproses approval');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Pending Approval</h1>
          <p className="text-gray-500 mt-1">Aplikasi kredit yang menunggu keputusan</p>
        </div>
        <span className="inline-flex items-center px-4 py-2 bg-amber-100 text-amber-700 rounded-lg text-lg font-medium">
          <Clock className="w-5 h-5 mr-2" />
          {applications.length} Menunggu
        </span>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg">
        <ApprovalTable
          applications={applications}
          loading={loading}
          getDebtorName={getDebtorName}
          onApprove={app => openApprovalDialog(app, 'approve')}
          onReject={app => openApprovalDialog(app, 'reject')}
        />
      </div>

      {/* Approval Dialog */}
      <ApprovalDialog
        isOpen={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        application={selectedApp}
        action={approvalAction}
        notes={approvalNotes}
        onNotesChange={setApprovalNotes}
        onConfirm={handleApproval}
        processing={processing}
        getDebtorName={getDebtorName}
      />
    </div>
  );
}
